/**
 * Token Storage Service
 * Handles secure storage and retrieval of authentication tokens
 */
const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const ID_TOKEN_KEY = 'auth_id_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';
/**
 * Store tokens in localStorage for persistence across page reloads
 * Tokens have expiry times and will be validated on each use
 * Note: Using localStorage instead of sessionStorage because sessionStorage
 * was not persisting tokens across reloads in some browser scenarios
 */
export function storeTokens(tokens) {
    try {
        const expiryTime = Date.now() + tokens.expiresIn * 1000;
        // Check localStorage quota before storing
        try {
            const testKey = '__storage_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
        }
        catch (e) {
            if (e.name === 'QuotaExceededError' || e.code === 22) {
                console.error('LocalStorage quota exceeded. Cannot store tokens.', e);
                throw new Error('LocalStorage quota exceeded. Cannot store tokens.');
            }
        }
        // Store tokens one by one and verify each
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        const accessTokenCheck = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (!accessTokenCheck || accessTokenCheck !== tokens.accessToken) {
            throw new Error('Failed to store accessToken - storage verification failed');
        }
        // Only store refreshToken if it exists (Azure AD SPA apps may not return it)
        if (tokens.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            const refreshTokenCheck = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (!refreshTokenCheck || refreshTokenCheck !== tokens.refreshToken) {
                throw new Error('Failed to store refreshToken - storage verification failed');
            }
        }
        else {
            // Remove any existing refreshToken if not provided
            localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
        localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
        const idTokenCheck = localStorage.getItem(ID_TOKEN_KEY);
        if (!idTokenCheck || idTokenCheck !== tokens.idToken) {
            throw new Error('Failed to store idToken - storage verification failed');
        }
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
        const expiryCheck = localStorage.getItem(TOKEN_EXPIRY_KEY);
        if (!expiryCheck || expiryCheck !== expiryTime.toString()) {
            throw new Error('Failed to store expiry - storage verification failed');
        }
    }
    catch (error) {
        console.error('Failed to store tokens:', error);
        throw error;
    }
}
/**
 * Get stored access token
 */
export function getAccessToken() {
    try {
        return localStorage.getItem(ACCESS_TOKEN_KEY);
    }
    catch (error) {
        console.error('Failed to get access token:', error);
        return null;
    }
}
/**
 * Get stored refresh token
 */
export function getRefreshToken() {
    try {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }
    catch (error) {
        console.error('Failed to get refresh token:', error);
        return null;
    }
}
/**
 * Get stored ID token
 */
export function getIdToken() {
    try {
        return localStorage.getItem(ID_TOKEN_KEY);
    }
    catch (error) {
        console.error('Failed to get ID token:', error);
        return null;
    }
}
/**
 * Check if token is expired
 */
export function isTokenExpired() {
    try {
        const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
        if (!expiryTime) {
            return true;
        }
        const expiryTimestamp = parseInt(expiryTime, 10);
        const currentTime = Date.now();
        return currentTime >= expiryTimestamp;
    }
    catch (error) {
        console.error('Failed to check token expiry:', error);
        return true;
    }
}
/**
 * Clear all stored tokens
 */
export function clearTokens() {
    try {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(ID_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
    }
    catch (error) {
        console.error('Failed to clear tokens:', error);
    }
}
/**
 * Get all stored tokens
 */
export function getTokens() {
    try {
        const accessToken = getAccessToken();
        const refreshToken = getRefreshToken();
        const idToken = getIdToken();
        const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
        // refreshToken is optional for Azure AD SPA apps
        if (!accessToken || !idToken || !expiryTime) {
            return null;
        }
        const expiresIn = Math.max(0, Math.floor((parseInt(expiryTime, 10) - Date.now()) / 1000));
        return {
            accessToken,
            refreshToken: refreshToken || null, // Optional - may be null for Azure AD SPA apps
            idToken,
            expiresIn,
            tokenType: 'Bearer',
        };
    }
    catch (error) {
        console.error('Failed to get tokens:', error);
        return null;
    }
}
//# sourceMappingURL=token-storage.service.js.map